import { Minus, Plus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Hours / Minutes / Seconds duration picker with stepper buttons.
 * @param {number} totalSeconds
 * @param {(seconds: number) => void} onChange
 */
export function DurationPicker({ totalSeconds, onChange }) {
    const { language } = useLanguage();
    const safe = Math.max(0, totalSeconds || 0);
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;

    const t = (de, en) => (language === 'de' ? de : en);

    const setParts = (nh, nm, ns) => {
        const total = Math.max(0, nh * 3600 + nm * 60 + ns);
        onChange(total);
    };

    const addSeconds = (delta) => onChange(Math.max(0, safe + delta));

    const Part = ({ label, value, max, onValueChange, testid }) => (
        <div className="flex flex-col items-center gap-1 flex-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            <div className="flex flex-col items-center bg-muted rounded-xl border-2 border-border w-full">
                <button
                    type="button"
                    onClick={() => onValueChange(Math.min(max, value + 1))}
                    className="w-full h-7 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-t-xl"
                    data-testid={`duration-${testid}-up`}
                >
                    <Plus className="w-3.5 h-3.5" />
                </button>
                <input
                    type="number"
                    min={0}
                    max={max}
                    value={value}
                    onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        onValueChange(Number.isNaN(v) ? 0 : Math.min(max, Math.max(0, v)));
                    }}
                    className="w-full text-center text-xl font-bold bg-transparent outline-none py-1"
                    data-testid={`duration-${testid}-input`}
                />
                <button
                    type="button"
                    onClick={() => onValueChange(Math.max(0, value - 1))}
                    className="w-full h-7 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-b-xl"
                    data-testid={`duration-${testid}-down`}
                >
                    <Minus className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );

    const STEPPERS = [
        { label: '+5 min', sec: 5 * 60 },
        { label: '+15 min', sec: 15 * 60 },
        { label: '+30 min', sec: 30 * 60 },
        { label: '+1 h', sec: 60 * 60 },
    ];

    return (
        <div className="space-y-2" data-testid="duration-picker">
            <div className="flex items-end gap-2">
                <Part label={t('Std', 'Hrs')} value={h} max={23} onValueChange={(v) => setParts(v, m, s)} testid="hours" />
                <span className="text-2xl font-bold text-muted-foreground pb-2">:</span>
                <Part label={t('Min', 'Min')} value={m} max={59} onValueChange={(v) => setParts(h, v, s)} testid="minutes" />
                <span className="text-2xl font-bold text-muted-foreground pb-2">:</span>
                <Part label={t('Sek', 'Sec')} value={s} max={59} onValueChange={(v) => setParts(h, m, v)} testid="seconds" />
            </div>
            <div className="grid grid-cols-4 gap-1.5">
                {STEPPERS.map((st) => (
                    <button
                        key={st.label}
                        type="button"
                        onClick={() => addSeconds(st.sec)}
                        className="py-1.5 px-2 text-xs font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        data-testid={`duration-stepper-${st.sec}`}
                    >
                        {st.label}
                    </button>
                ))}
            </div>
            {safe > 0 && (
                <button
                    type="button"
                    onClick={() => onChange(0)}
                    className="w-full text-xs text-muted-foreground hover:text-foreground py-1"
                    data-testid="duration-reset"
                >
                    {t('Zurücksetzen', 'Reset')}
                </button>
            )}
        </div>
    );
}
